import { components } from "@/types/openapi";

export interface PermissionFunctions {
  canAccessPanel: (panel: string) => boolean;
  canAdministerCompetition: (competition: string) => boolean;
  canScoretakeCompetition: (competition: string) => boolean;
  canAttendCompetition: (competition: string) => boolean;
  canOrganizeCompetitions: (competition: string) => boolean;
  canEditDelegateReport: (competition: string) => boolean;
  canViewDelegateReport: (competition: string) => boolean;
  canCreateGroup: (group: string) => boolean;
  canEditGroup: (group: string) => boolean;
  canReadGroupCurrent: (group: string) => boolean;
  canReadGroupPast: (group: string) => boolean;
  canRequestToEditProfile: (profile: string) => boolean;
  // `can_manage_incidents` is granted to WRC and admins for every incident or none, so unlike its
  // siblings its scope is never a list of ids and there is nothing to pass in. The same holds for
  // `can_view_delegate_admin_page` and `can_organize_competitions`.
  canManageIncidents: () => boolean;
  canViewDelegateAdminPage: () => boolean;
  canViewPolls: () => boolean;
  canViewAllUsers: () => boolean;
  canAdminResults: () => boolean;
  canCreatePosts: () => boolean;
  canManageRegionalOrganizations: () => boolean;
  canAccessCms: () => boolean;
  canCreateCompetitions: () => boolean;
}

export type UserPermissions = components["schemas"]["UserPermissions"];

export interface PermissionContext extends PermissionFunctions {
  permissions?: UserPermissions;
}

const allOrSpecificScope = (
  item: string,
  scope: components["schemas"]["CompetitionPermissions"],
) => {
  return scope === "*" || scope.includes(item);
};

export const hydrateUserPermissions = (
  rawPermissions?: UserPermissions,
): PermissionFunctions => ({
  canAccessPanel: (panel) =>
    Boolean(
      rawPermissions && rawPermissions.can_access_panels.scope.includes(panel),
    ),
  canAdministerCompetition: (competition) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(
        competition,
        rawPermissions.can_administer_competitions.scope,
      ),
    ),
  canScoretakeCompetition: (competition) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(
        competition,
        rawPermissions.can_scoretake_competitions.scope,
      ),
    ),
  canAttendCompetition: (competition) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(
        competition,
        rawPermissions.can_attend_competitions.scope,
      ),
    ),
  canOrganizeCompetitions: (competition) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(
        competition,
        rawPermissions.can_organize_competitions.scope,
      ),
    ),
  canEditDelegateReport: (competition) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(
        competition,
        rawPermissions.can_edit_delegate_report.scope,
      ),
    ),
  canViewDelegateReport: (competition) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(
        competition,
        rawPermissions.can_view_delegate_report.scope,
      ),
    ),
  canCreateGroup: (group) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(group, rawPermissions.can_create_groups.scope),
    ),
  canEditGroup: (group) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(group, rawPermissions.can_edit_groups.scope),
    ),
  canReadGroupCurrent: (group) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(group, rawPermissions.can_read_groups_current.scope),
    ),
  canReadGroupPast: (group) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(group, rawPermissions.can_read_groups_past.scope),
    ),
  canRequestToEditProfile: (profile) =>
    Boolean(
      rawPermissions &&
      allOrSpecificScope(
        profile,
        rawPermissions.can_request_to_edit_others_profile.scope,
      ),
    ),
  canManageIncidents: () => rawPermissions?.can_manage_incidents.scope === "*",
  canViewDelegateAdminPage: () =>
    rawPermissions?.can_view_delegate_admin_page.scope === "*",
  canViewPolls: () => rawPermissions?.can_view_polls.scope === "*",
  canViewAllUsers: () => rawPermissions?.can_view_all_users.scope === "*",
  canAdminResults: () => rawPermissions?.can_admin_results.scope === "*",
  canCreatePosts: () => rawPermissions?.can_create_posts.scope === "*",
  canManageRegionalOrganizations: () =>
    rawPermissions?.can_manage_regional_organizations.scope === "*",
  canAccessCms: () => rawPermissions?.can_access_cms.scope === "*",
  canCreateCompetitions: () =>
    rawPermissions?.can_organize_competitions.scope === "*",
});
